import { http, createConfig } from "wagmi";
// import environment from "./environment";
import { Chain } from "viem";
// import { ChainDetailType } from "./types/wallet";
import { walletConnect, injected, coinbaseWallet } from "wagmi/connectors";
// import { metaMask } from "./providers/adapter/eth/metamask";
import environment from "./src/environment";
import { ChainDetailType } from "./src/interfaces/environment";
import { getEnv } from "./src/utils/env";

const projectId = getEnv("REACT_APP_WALLET_CONNECT_PROJECT_ID");

// create chains list from environment
const parseChainData = (
  chainData: ChainDetailType.Item[]
): readonly [Chain, ...Chain[]] => {
  const chains = chainData.map((data) => {
    const chain: Chain = {
      id: data.chainId,
      name: data.name,
      nativeCurrency: {
        name: data.currencyName,
        symbol: data.currencySymbol,
        decimals: data.currencyDecimal,
      },
      rpcUrls: {
        default: {
          http: data.rpcUrls,
        },
      },
      blockExplorers: {
        default: {
          name: `${data.name} Explorer`,
          url: data.explorerUrls[0],
        },
      },
      contracts: {},
    };
    return chain;
  });

  // filter null chains
  const filteredChains = chains.filter((chain) => chain !== undefined);

  if (filteredChains.length === 0) {
    throw new Error("Chain data cannot be empty");
  }

  return filteredChains as unknown as readonly [Chain, ...Chain[]];
};
const parseTransport = (chain: Chain) => {
  return {
    [chain.id]: http(chain.rpcUrls.default.http[0] as string, {
      retryCount: 3,
      retryDelay: 1000,
      timeout: 10000,
    }),
  };
};

export const chains = parseChainData(environment.allowToLoginChains);
const transports = chains
  .map(parseTransport)
  .reduce((acc, val) => ({ ...acc, ...val }), {});

const getGateWalletProvider = () => {
  if (typeof window === "undefined") return undefined;
  const gate = window.gatewallet;
  if (!gate) return undefined;
  if (Array.isArray(gate.providers)) {
    return (
      gate.providers.find(
        (p: any) =>
          p?.isWeb3Wallet ||
          p?.isGateWallet ||
          typeof p?.request === "function"
      ) ?? gate.providers[0]
    );
  }
  return typeof gate.request === "function" ? gate : undefined;
};

export const config = createConfig({
  chains: chains,
  connectors: projectId
    ? [
        // injected({
        //   target() {
        //     return {
        //       id: "bitgetWallet",
        //       name: "Bitget Wallet",
        //       provider: window.bitkeep?.ethereum,
        //     };
        //   },
        walletConnect({ projectId }),
        coinbaseWallet(),
        injected({
          target() {
            return {
              id: "universeWallet",
              name: "Universe Wallet",
              provider: window.universeWallet ?? window.ethereum,
            };
          },
        }),
        injected({
          target() {
            return {
              id: "gateWallet",
              name: "GateWallet",
              provider: getGateWalletProvider,
            };
          },
        }),
        injected({
          target() {
            return {
              id: "okxWallet",
              name: "OKX Wallet",
              provider: window.okxwallet ?? undefined,
            };
          },
        }),
        injected({
          target() {
            return {
              id: "safepal",
              name: "SafePal",
              provider: window.safepalProvider ?? undefined,
            };
          },
        }),
        injected({
          target: {
            id: "wallet.binance.com",
            name: "Binance Wallet Injected",
            provider: () =>
              window.binancew3w?.ethereum ??
              window.BinanceChain ??
              window.ethereum?.providers?.find((p: any) => p.isBinance),
          },
        }),
      ]
    : [
        injected({
          target() {
            return {
              id: "universeWallet",
              name: "Universe Wallet",
              provider: window.universeWallet ?? window.ethereum,
            };
          },
        }),
        injected({
          target() {
            return {
              id: "gateWallet",
              name: "GateWallet",
              provider: getGateWalletProvider,
            };
          },
        }),
        coinbaseWallet(),
        injected({
          target() {
            return {
              id: "okxWallet",
              name: "OKX Wallet",
              provider: window.okxwallet ?? undefined,
            };
          },
        }),
        injected({
          target() {
            return {
              id: "safepal",
              name: "SafePal",
              provider: window.safepalProvider ?? undefined,
            };
          },
        }),
        injected({
          target: {
            id: "wallet.binance.com",
            name: "Binance Wallet Injected",
            provider: () =>
              window.binancew3w?.ethereum ??
              window.BinanceChain ??
              window.ethereum?.providers?.find((p: any) => p.isBinance),
          },
        }),
      ],
  multiInjectedProviderDiscovery: true,
  syncConnectedChain: true, // sync connected chainId with the connector
  ssr: false,
  transports: transports,
});

declare global {
  interface Window {
    gatewallet?: any;
    okxwallet?: any;
    safepalProvider?: any;
    universeWallet?: any;
    BinanceChain?: any;
    binancew3w?: {
      ethereum?: any;
    };
  }
}

declare module "wagmi" {
  interface Register {
    config: typeof config;
  }
}
